import { ref, computed, onMounted, onUnmounted } from 'vue'

/*
  Use Tailwind-style breakpoints:
    sm   >= 640
    md   >= 768
    lg   >= 1024
    xl   >= 1280
    2xl  >= 1536
    2xl  >= 1700

  Note: to preserve existing consumer logic that used "isSmallTierOrLess"
  and "isMediumTierOrLess" we keep those export names but base them on
  the new tier ordering.
*/
type SizeTier = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl'

export function useWindowSize() {
  const windowWidth = ref<number>(typeof window !== 'undefined' ? window.innerWidth : 0)
  const updateWindowWidth = () =>
    (windowWidth.value = typeof window !== 'undefined' ? window.innerWidth : 0)

  onMounted(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateWindowWidth, { passive: true })
    }
  })

  onUnmounted(() => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', updateWindowWidth)
    }
  })

  // size tiers: sm (>=640), md (>=768), lg (>=1024), xl (>=1280), 2xl (>=1536)
  const sizeTier = computed<SizeTier>(() => {
    const w = windowWidth.value
    if (w >= 1700) return '3xl'
    if (w >= 1536) return '2xl'
    if (w >= 1280) return 'xl'
    if (w >= 1150) return 'lg'
    if (w >= 768) return 'md'
    // Treat everything below md as "sm" tier for our app-level logic
    return 'sm'
  })

  // ordinal map to allow "or less" comparisons (sm < md < lg < xl < 2xl)
  const tierOrder: Record<SizeTier, number> = { sm: 0, md: 1, lg: 2, xl: 3, '2xl': 4, '3xl': 5 }

  // helper function: check whether current tier is <= provided tier
  const isTierOrLess = (tier: SizeTier) => tierOrder[sizeTier.value] <= tierOrder[tier]

  // computed helpers (kept names compatible with existing app code)
  const isSmallTierOrLess = computed(() => isTierOrLess('sm')) // <= sm (i.e. below md)
  const isMediumTierOrLess = computed(() => isTierOrLess('md')) // <= md (i.e. below lg)
  const isLargeTierOrLess = computed(() => isTierOrLess('lg')) // <= lg (i.e. below xl)
  const isExtraLargeTierOrLess = computed(() => isTierOrLess('xl')) // <= lg (i.e. below xl)
  const is2XLargeTierOrLess = computed(() => isTierOrLess('2xl')) // <= lg (i.e. below 2xl)
  const is3XLargeTierOrLess = computed(() => isTierOrLess('3xl')) // <= lg (i.e. below 3xl)

  // helper function: check whether current tier is >= provided tier
  const isTierOrGreater = (tier: SizeTier) => tierOrder[sizeTier.value] >= tierOrder[tier]

  const isSmallTierOrGreater = computed(() => isTierOrGreater('sm'))
  const isMediumTierOrGreater = computed(() => isTierOrGreater('md'))
  const isLargeTierOrGreater = computed(() => isTierOrGreater('lg'))
  const isExtraLargeTierOrGreater = computed(() => isTierOrGreater('xl'))
  const is2XLargeTierOrGreater = computed(() => isTierOrGreater('2xl'))
  const is3XLargeTierOrGreater = computed(() => isTierOrGreater('3xl'))

  const isWindowWidthAtLeast = (px: number) => windowWidth.value >= px
  const isWindowWidthAtMost = (px: number) => windowWidth.value <= px

  // New: map size tiers to Ant Design control sizes:
  // - 'sm'  -> 'small'
  // - 'lg'  -> 'middle'
  // - '2xl' -> 'large'
  // default -> 'middle'
  const controlSize = computed<'small' | 'middle' | 'large'>(() => {
    if (sizeTier.value === 'md') return 'middle'
    if (sizeTier.value === 'lg') return 'middle'
    if (sizeTier.value === 'xl') return 'large'
    if (sizeTier.value === '2xl') return 'large'
    if (sizeTier.value === '3xl') return 'large'
    // for md and xl (and any other), use small size
    return 'small'
  })

  return {
    windowWidth,
    sizeTier,
    isTierOrLess,
    isSmallTierOrLess,
    isMediumTierOrLess,
    isLargeTierOrLess,
    isExtraLargeTierOrLess,
    is2XLargeTierOrLess,
    is3XLargeTierOrLess,
    controlSize,
    isTierOrGreater,
    isSmallTierOrGreater,
    isMediumTierOrGreater,
    isLargeTierOrGreater,
    isExtraLargeTierOrGreater,
    is2XLargeTierOrGreater,
    is3XLargeTierOrGreater,
    isWindowWidthAtLeast,
    isWindowWidthAtMost
  }
}
